<template>
  <div class="auth">
    <div class="login-form">
      <div class="form-left">
        <div class="mb-10">
          <img alt="BeMob" src="/images/logo-white2.svg" />
        </div>
        <p class="mb-32 pr-30">
          {{
            t(
              'BeMob helps to manage your ad campaigns, identify the most profitable traffic sources, measure your landing pages performance and provide highly detailed real-time reports.',
            )
          }}
        </p>
        <div class="copyright">
          2015-{{ moment().year() }}
          {{ t('© BeMob. All rights reserved.') }}
        </div>
      </div>

      <div class="form-right">
        <div class="form-right-inner">
          <div class="login-title">{{ t('Login to account') }}</div>
          <div class="error-block mb-22 d-flex align-center justify-start" v-if="apiErrors">
            <div class="error-text">
              <ui-icon size="12" class="mr-8" icon="fas fa-exclamation-triangle" />
              {{ apiErrors }}
            </div>
          </div>
          <ui-input
            class="ui-input__login"
            autofocus
            @keyup.enter="submit"
            append-inner-icon="fas fa-user"
            :label="t('UserId')"
            :placeholder="t('UserId')"
            type="text"
            v-model="form.userId"
            name="userId"
          />
          <ui-input
            class="ui-input__login"
            :label="t('Password')"
            :placeholder="t('Password')"
            v-model="form.password"
            name="password"
            type="password"
            append-inner-icon="fas fa-key"
            @keyup.enter="submit"
          />
          <div v-if="showRecaptcha" class="login-recaptcha">
            <RecaptchaV2
              :key="recaptchaResetKey"
              @expired-callback="onRecaptchaExpired"
              @load-callback="onRecaptchaVerify"
            />
          </div>
          <ui-button
            button-style="contained"
            :loading="apiLoading"
            :disabled="showRecaptcha && !form.recaptchaResponse"
            @click="submit"
            class="w100"
            iconAppend="fas fa-arrow-right"
            :text="t('Sign in')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RecaptchaV2 } from 'vue3-recaptcha-v2';

import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment-timezone';
import { useI18n } from '@/composables';
import { loginSupportApiRequest } from '@/services/api/auth/auth-login-support-api';
import type { LoginSupportForm } from '@/services/api/auth/auth-login-support-api';
import { setAuthToken } from '@/services/api/main/axios';
import { useStore } from '@/store';
import { performLogoutCleanup } from '@/services/logout';

const store = useStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const showRecaptcha = ref(false);
const recaptchaResetKey = ref(0);

const form = reactive({
  userId: '',
  password: '',
  recaptchaResponse: '',
});

const apiLoading = ref(false);
const apiErrors = ref('');

const onShowRecaptcha = () => {
  showRecaptcha.value = true;
};

const onRecaptchaReset = () => {
  form.recaptchaResponse = '';
  recaptchaResetKey.value++;
};

const onClearPassword = () => {
  form.password = '';
};

const apiSubmit = async (loginForm: LoginSupportForm) => {
  apiErrors.value = '';
  apiLoading.value = true;

  try {
    const res = await loginSupportApiRequest(loginForm);
    const payload = res.data.payload;

    if (store.isAuthenticated) {
      performLogoutCleanup();
    }

    setAuthToken(payload.id);

    if (payload.ttl === 0) {
      return router.push('/two-factor');
    }
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    onRecaptchaReset();

    if (status === 401 || status === 422) {
      onShowRecaptcha();
    }

    if (status === 400 || status === 401 || status === 403 || status === 429) {
      onClearPassword();
    }

    if (status === 429) {
      apiErrors.value = t("You've reached the maximum login attempts. Try again in 60 minutes.");
    }

    if (status === 400 || status === 401 || status === 403) {
      apiErrors.value = t('Email / Password is invalid');
    }

    throw err;
  } finally {
    apiLoading.value = false;
  }
};

onMounted(() => {
  form.userId = route.query?.userId?.toString() ?? '';
});

const onRecaptchaVerify = (response: string) => {
  form.recaptchaResponse = response;
};

const onRecaptchaExpired = () => {
  form.recaptchaResponse = '';
};

const submit = () => {
  apiSubmit(form);
};
</script>

